import type React from 'react';
import { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useActiveEditor } from './TiptapEditor/ActiveEditorContext';

export interface BlockInfo {
  blockId: string | null;
  sectionId: string | null;
}

export interface SelectedBlockInfoContextProps {
  setSelectedBlockInfoByWrapper: (selectedBlockInfoByWrapper: BlockInfo) => void;
  handleSelectedBlockInfoByWrapper: (selectedBlockInfoByWrapper: BlockInfo) => void;
  selectedBlockInfoByWrapper: BlockInfo;
  selectedBlockIdByWrapper: string | null;
  selectedSectionId: string | null;
}

export const SelectionContext = createContext<SelectedBlockInfoContextProps>({} as SelectedBlockInfoContextProps);

export const useSelectionContext = () => useContext(SelectionContext);

interface SelectedBlockInfoProviderProps {
  children: React.ReactNode;
}

export const SelectedBlockInfoProvider = ({ children }: SelectedBlockInfoProviderProps) => {
  const [selectedBlockInfoByWrapper, setSelectedBlockInfoByWrapper] = useState<BlockInfo>({ blockId: null, sectionId: null });
  const { setActiveEditor } = useActiveEditor();
  const previousBlockIdRef = useRef(selectedBlockInfoByWrapper.blockId);

  const handleSelectedBlockInfoByWrapper = useCallback(({ blockId, sectionId }: BlockInfo) => {
    setSelectedBlockInfoByWrapper((prev) => {
      if (blockId === prev.blockId && sectionId === prev.sectionId) return prev;
      return { blockId, sectionId };
    });
  }, []);

  /**
   * Whichever block was selected before may still be the active TipTap editor.
   * leaving the editor active keeps its selection-highlight decoration painted on the block after
   * it is deselected so the editor must be explicitly dropped here whenever the selected block changes.
   */
  useEffect(() => {
    if (previousBlockIdRef.current !== selectedBlockInfoByWrapper.blockId) {
      setActiveEditor(null);
    }
    previousBlockIdRef.current = selectedBlockInfoByWrapper.blockId;
  }, [selectedBlockInfoByWrapper.blockId, setActiveEditor]);

  const contextValue = useMemo<SelectedBlockInfoContextProps>(
    () => ({
      selectedBlockInfoByWrapper,
      selectedBlockIdByWrapper: selectedBlockInfoByWrapper.blockId,
      setSelectedBlockInfoByWrapper,
      handleSelectedBlockInfoByWrapper,
      selectedSectionId: selectedBlockInfoByWrapper.sectionId,
    }),
    [selectedBlockInfoByWrapper, handleSelectedBlockInfoByWrapper]
  );

  return <SelectionContext.Provider value={contextValue}>{children}</SelectionContext.Provider>;
};
